import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {RawNewInfoInterface} from '../raw-new-Info.interface';
import {
    Column,
    ConfiguratorDatastoreService,
    ConfiguratorSendInBlueService,
    ConfiguratorTableService,
    DataViewDto,
    EngineDataQueryService,
    FieldTableDto,
    KeyValueOflongAndstring,
    Table,
    UpdateData,
    UserDTO
} from '@tj/tj-dmp-lib-api';
import {AbstractEditorComponent} from '../../../field/field-editor/abstract-editor.component';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {AccountService} from '../../../../auth/account.service';
import {isAdminOfTenant} from '../../../../auth/admin-levels';
import {switchMap, tap} from 'rxjs/operators';
import {of} from 'rxjs';
import {FieldService} from '../../../field/field/field.service';

type TemplateSendingblue = KeyValueOflongAndstring;

@Component({
    selector: 'dmp-dyn-raw-new',
    templateUrl: './dyn-raw-new.component.html',
    styleUrls: ['./dyn-raw-new.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class DynRawNewComponent implements OnInit {

    @Input() configCmp: RawNewInfoInterface;
    @Input() enableTemplatesListing = true;
    @Output() saved = new EventEmitter<boolean>();
    @Output() cancelled = new EventEmitter<void>();

    private fieldsEditor = new Set<AbstractEditorComponent>();
    private mandatoryFields: string[] = [];

    rowData: any = {};
    private dataview: DataViewDto;
    fieldCols: FieldTableDto[] = [];

    private isPartnerUser = false;
    private isAdminOrTenant = false;
    private userPartnerCode: string = null;

    sendingblueTemplates: TemplateSendingblue[] = [];
    sendingblueTemplate: number;

    constructor(protected tableResourceService: ConfiguratorTableService,
                private dataEngine: EngineDataQueryService,
                private messageService: MessageService,
                private translateService: TranslateService,
                private sendingblueService: ConfiguratorSendInBlueService,
                private accountService: AccountService,
                private datastoreService: ConfiguratorDatastoreService,
                private fieldService: FieldService) {
    }

    ngOnInit(): void {
        this.fieldService.datastore = this.configCmp.datastore;
        this.dataview = this.configCmp.dataView;
        const dataviewType = this.dataview.code.split('-')[0];
        if (dataviewType === 'personal'){
            this.mandatoryFields = ['personal.email', `personal_affilie_${this.configCmp.partner.toLowerCase()}.type_current`];
        } else if (dataviewType === 'command'){
            this.mandatoryFields = ['personal.email'];
        }

        // Determine user role and partner code
        this.accountService.fetch().pipe(
            switchMap((account: UserDTO) => {
                this.isAdminOrTenant = isAdminOfTenant(account, this.configCmp.datastore);

                if (this.isAdminOrTenant) {
                    this.isPartnerUser = false;
                    this.userPartnerCode = null;

                    // Add affiliate_id to mandatory fields BEFORE loading fields
                    if (dataviewType === 'command') {
                        const affiliateFieldKey = 'command.affiliate_id';
                        if (!this.mandatoryFields.includes(affiliateFieldKey)) {
                            this.mandatoryFields.push(affiliateFieldKey);
                        }
                    }
                    return of(null);
                } else {
                    return this.datastoreService.getDatastoreAccessiblePartnersUsingGET(
                        this.configCmp.datastore,
                        true  // isCode = true
                    ).pipe(
                        tap(partners => {
                            if (partners && partners.length === 1) {
                                // User has access to exactly one partner == is a partner user
                                this.isPartnerUser = true;
                                this.userPartnerCode = partners[0];
                            } else {
                                // User has access to multiple partners or none
                                this.isAdminOrTenant = true;
                                this.isPartnerUser = false;
                                if (dataviewType === 'command') {
                                    const affiliateFieldKey = 'command.affiliate_id';
                                    if (!this.mandatoryFields.includes(affiliateFieldKey)) {
                                        this.mandatoryFields.push(affiliateFieldKey);
                                    }
                                }
                            }
                        })
                    );
                }
            }),
            switchMap(() => {
                const codeTables = this.dataview.selectColumns.map(c => c.tableName);
                return this.tableResourceService.getTablesUsingGET(this.configCmp.datastore, codeTables);
            })
        ).subscribe(tables => {
            this.loadField(tables, this.dataview.defaultFieldCreation, this.fieldCols, this.rowData);

            // Handle affiliate_id based on user type
            if (dataviewType === 'command') {
                if (this.isPartnerUser) {
                    // Remove affiliate_id from visible fields for partner users
                    this.fieldCols = this.fieldCols.filter(f =>
                        !(f.table === 'command' && f.code === 'affiliate_id')
                    );

                    // Auto-fill affiliate_id in rowData
                    if (this.rowData.command) {
                        this.rowData.command.affiliate_id = this.userPartnerCode;
                    } else {
                        this.rowData.command = { affiliate_id: this.userPartnerCode };
                    }
                } else if (this.isAdminOrTenant) {
                    // Set notNull property on affiliate_id field to show required indicator
                    const affiliateField = this.fieldCols.find(f =>
                        f.table === 'command' && f.code === 'affiliate_id'
                    );
                    if (affiliateField) {
                        affiliateField.notNull = true;
                    }
                }
            }
        });

        if (this.enableTemplatesListing){
            this.listingSendingblueTemplate();
        }
    }

    listingSendingblueTemplate() {
        this.sendingblueService.getAllTransactionalEmails1(this.configCmp.datastore).subscribe((res: TemplateSendingblue[]) => {
        this.sendingblueTemplates = res;
        this.sendingblueTemplate = this.sendingblueTemplates[0]?.key;
       });
    }

    private loadField(tables: Table[], defaulFieldCol: Array<Column>, fieldCol: FieldTableDto[], rowData: any) {

        const cols = defaulFieldCol.map(dField => `${dField.tableName}.${dField.propertyName}`);

        this.mandatoryFields.forEach(field => {
            const col = cols.find(c => c === field);
            if (col === undefined){
                cols.push(field);
            }
        });

        cols?.forEach(dField => {
            const [fieldTable, fieldCode] = dField.split('.');
            const t = tables.find(table => table.code === fieldTable);
            if (!t) {
                // Les tables chargées proviennent des seules `selectColumns` de la vue : un champ
                // ajouté par `mandatoryFields` (ex. `personal_affilie_<partenaire>.type_current`)
                // peut donc viser une table absente. Sans ce garde, le `rowData[t.code]` ci-dessous
                // levait un TypeError qui interrompait la boucle : tous les champs restants
                // disparaissaient du formulaire sans le moindre message.
                console.warn(`[dyn-raw-new] Table "${fieldTable}" absente des tables de la vue ` +
                    `"${this.dataview?.code}" : le champ "${dField}" n'est pas affiché.`);
                return;
            }
            const f = t.fields.find(field => field.code === fieldCode);
            if (rowData[t.code] == null) {
                rowData[t.code] = {};
            }
            if (f) {
                rowData[t.code][f.code] = null;
                const col = f as FieldTableDto;
                col.table = t.code;
                col.label = col.label.length > 0 ? col.label : col.code;
                fieldCol.push(col);
            } else {
                console.warn(`[dyn-raw-new] Champ "${fieldCode}" introuvable dans la table ` +
                    `"${fieldTable}" : il n'est pas affiché.`);
            }
        });
    }

    save() {
        let nbErrorField = 0;
        this.fieldsEditor.forEach(f => {
            if (!f.isValid()) {
                nbErrorField++;
            }
        });

        const dataviewType = this.dataview.code.split('-')[0];
        if (dataviewType === 'command' && this.isAdminOrTenant) {
            const affiliateId = this.rowData.command.affiliate_id;
            if (!affiliateId || affiliateId.trim() === '') {
                this.messageService.add({
                    key: 'local',
                    severity: 'error',
                    summary: this.translateService.instant('error.form.errors'),
                    detail: this.translateService.instant('error.affiliate_id.required') ||
                            'Le champ affiliate_id est obligatoire'
                });
                return;
            }
        }

        if (nbErrorField > 0) {
            this.messageService.add({
                key: 'local',
                severity: 'error',
                summary: this.translateService.instant('error.form.errors'),
            });
        } else {

            const idForTable = Object.keys(this.rowData).reduce((acc, res) => {
                acc[`${res}.id`] = null;
                return acc;
            }, {});

            const newData: UpdateData = {
                idForTable,
                idDataview: this.dataview.id,
                tenant: this.configCmp.datastore,
                partner: this.configCmp.partner,
                updateField: {}
            };
            Object.keys(this.rowData).forEach(table => {
                Object.keys(this.rowData[table]).forEach(field => {
                    const value = this.rowData[table][field];
                    if (value != null){

                        const valueParts = value.split('|');
                        if (valueParts.length === 1){
                            newData.updateField[table + '.' + field] = value;
                        } else {
                            if (table.includes('personal_affilie') && field === 'type_current') {
                                const typeCurrent = valueParts[0];
                                newData.updateField[table + '.' + field] = typeCurrent;
                                newData.updateField[table + '.type_' + typeCurrent.toLowerCase()] = valueParts[1];
                            }
                        }
                    }
                });
            });
            this.dataEngine.newData(newData, this.configCmp.datastore).subscribe(res => {
                this.saved.next(true);
            });
        }
    }

    doInitEditor($event: AbstractEditorComponent) {
        this.fieldsEditor.add($event);
    }
}
